import React from "react";
import { useTheme } from "styled-components";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { FiDollarSign, FiCalendar } from "react-icons/fi";
import {
  WidgetGrid, Widget, Section, Skeleton, tokens,
} from "../../activity-logs/styles";
import { Sparkline } from "../../activity-logs/components/primitives";
import { IOS } from "../../activity-logs/labels";
import { CompositionWidget, PeriodMap, PeriodStats } from "../styles";
import { ChartTooltip } from "./charts";
import { MoneyValue } from "./parts";
import TopLists from "./TopLists";
import CategoryBento from "./CategoryBento";
import {
  TAXES, MONTHS, formatCurrency, formatCompact, formatCount, formatPercent,
} from "../dashboardData";

function MonthlyChart({ months, animate }) {
  const theme = useTheme();
  const axis = {
    tick: { fontSize: 10, fill: tokens.label2(theme) },
    axisLine: false,
    tickLine: false,
  };
  const grid = <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />;
  const x = <XAxis dataKey="label" minTickGap={18} {...axis} />;
  const y = <YAxis width={70} tickFormatter={formatCompact} {...axis} />;
  const margin = { top: 10, right: 4, left: 0, bottom: 0 };

  // Área precisa de pelo menos 3 pontos para fazer sentido; com menos, barras
  if (months.length < 3) {
    return (
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={months} margin={margin}>
          {grid}{x}{y}
          <Tooltip content={<ChartTooltip />} cursor={{ fill: tokens.fill(theme) }} />
          {TAXES.map((t) => <Bar key={t.key} dataKey={t.dataKey} name={t.label} stackId="1" fill={t.color} fillOpacity={0.8} maxBarSize={32} isAnimationActive={animate} />)}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={230}>
      <AreaChart data={months} margin={margin}>
        <defs>
          {TAXES.map((t) => (
            <linearGradient key={t.key} id={`cr-${t.dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={t.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={t.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        {grid}{x}{y}
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
        {TAXES.map((t) => (
          <Area key={t.key} type="monotone" dataKey={t.dataKey} name={t.label} stackId="1" stroke={t.color} strokeWidth={1.5} fill={`url(#cr-${t.dataKey})`} isAnimationActive={animate} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function Composition({ kpis }) {
  const theme = useTheme();
  const rows = TAXES.map((t) => ({ ...t, value: kpis[t.kpi] || 0 }));
  const sum = rows.reduce((acc, r) => acc + r.value, 0);

  return (
    <>
      <div className="w-title">Composição</div>
      <div className="w-sub">Participação de cada imposto</div>
      <div style={{ position: "relative", height: 150, marginTop: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={sum > 0 ? rows : [{ key: "vazio", value: 1 }]}
              dataKey="value"
              nameKey="label"
              innerRadius={56}
              outerRadius={68}
              cornerRadius={4}
              paddingAngle={sum > 0 ? 3 : 0}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {sum > 0
                ? rows.map((r) => <Cell key={r.key} fill={r.color} />)
                : <Cell fill={tokens.fill(theme)} />}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", fontSize: "1em", fontWeight: 600, letterSpacing: "-0.02em" }}>
          {formatCompact(sum)}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
        {rows.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8em" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 500 }}>{r.label}</span>
            <span style={{ color: tokens.label2(theme) }}>
              {formatPercent(sum > 0 ? (r.value / sum) * 100 : 0)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function PeriodHeatmap({ months, onSelect }) {
  const max = Math.max(1, ...months.map((m) => m.total));
  const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
  const years = [...new Set(months.map((m) => m.key.slice(0, 4)))];
  const withCredit = months.filter((m) => m.total > 0);
  const best = withCredit.reduce((top, m) => (!top || m.total > top.total ? m : top), null);
  const average = withCredit.length > 0 ? withCredit.reduce((sum, m) => sum + m.total, 0) / withCredit.length : 0;

  return (
    <>
    <PeriodMap>
      <div />
      {MONTHS.map((m) => <div key={m} className="head">{m}</div>)}
      {years.map((year) => (
        <React.Fragment key={year}>
          <div className="year">{year}</div>
          {MONTHS.map((name, i) => {
            const key = `${year}-${String(i + 1).padStart(2, "0")}`;
            const total = byKey[key]?.total || 0;
            return (
              <button
                key={key}
                type="button"
                className="cell"
                disabled={!total}
                title={`${name}/${year} — ${formatCurrency(total)}`}
                aria-label={`Filtrar ${name} de ${year}, ${formatCurrency(total)}`}
                onClick={() => onSelect(key)}
              >
                {total > 0 && <span className="fill" style={{ opacity: 0.12 + (total / max) * 0.68 }} />}
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </PeriodMap>
    <PeriodStats>
      <div>
        <span className="v" title={best ? formatCurrency(best.total) : undefined}>{best ? best.label : "—"}</span>
        <span className="l">Maior mês{best ? ` · ${formatCompact(best.total)}` : ""}</span>
      </div>
      <div>
        <span className="v" title={formatCurrency(average)}>{formatCompact(average)}</span>
        <span className="l">Média por mês com crédito</span>
      </div>
      <div>
        <span className="v">{withCredit.length}</span>
        <span className="l">{withCredit.length === 1 ? "Mês com crédito" : "Meses com crédito"}</span>
      </div>
    </PeriodStats>
    </>
  );
}

export default function Overview({
  kpis, dashboard, series, suppliers, periodFiltered, loading,
  onGoCredits, onGoSuppliers, onOpenCredit, onOpenSupplier, onSelectMonth,
}) {
  const theme = useTheme();
  const { categories, topCreditos, itemCount } = dashboard;
  const { months } = series;
  const total = kpis.totalCreditos || 0;

  const peak = months.reduce((best, m) => (!best || m.total > best.total ? m : best), null);
  const waitingSeries = series.loading && months.length === 0;
  const outsideChart = total - months.reduce((sum, m) => sum + m.total, 0);

  const value = (v) => (loading ? <Skeleton $w="70%" $h={30} /> : <MoneyValue value={v} />);

  let seriesCaption;
  if (series.loading) seriesCaption = "Consultando a API mês a mês...";
  else if (series.failed) seriesCaption = "Não foi possível consultar todos os meses: série incompleta";
  else if (!series.complete && outsideChart >= total * 0.005) seriesCaption = "Série parcial: há valores fora dos meses consultados";
  else if (peak) {
    seriesCaption = (
      <>
        Pico: <strong style={{ color: tokens.label(theme) }}>{peak.label}</strong> · {formatCurrency(peak.total)}
        {!series.complete && ` · ${formatCurrency(outsideChart)} em documentos fora do período do gráfico`}
      </>
    );
  } else seriesCaption = "Valor por mês de emissão do documento";

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget data-tour="kpi-total" $delay={0} $clickable onClick={() => onGoCredits()}>
            <div className="w-head"><FiDollarSign style={{ color: IOS.mint, opacity: 1 }} /> Total em créditos</div>
            <div className="w-value">{value(total)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatCount(itemCount)} {itemCount === 1 ? "item" : "itens"}</span>
              <Sparkline data={months.map((m) => m.total)} color={IOS.mint} width={80} height={24} points={60} />
            </div>
          </Widget>

          {TAXES.map((t, i) => {
            const amount = kpis[t.kpi] || 0;
            return (
              <Widget data-tour="kpi-tax" key={t.key} $delay={(i + 1) * 50} $clickable onClick={() => onGoCredits({ tax: t.key })}>
                <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: t.color, opacity: 1 }} /> {t.label}</div>
                <div className="w-value">{value(amount)}</div>
                <div className="w-foot" style={{ justifyContent: "space-between" }}>
                  <span>{formatPercent(total > 0 ? (amount / total) * 100 : 0)} do total</span>
                  <Sparkline data={months.map((m) => m[t.dataKey])} color={t.color} width={80} height={24} points={60} />
                </div>
              </Widget>
            );
          })}
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget data-tour="trend" $span={3} $delay={150}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
              <div>
                <div className="w-title">Evolução mensal</div>
                <div className="w-sub">{seriesCaption}</div>
              </div>
              <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
                {TAXES.map((t) => (
                  <span key={t.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.color }} />{t.label}
                  </span>
                ))}
              </div>
            </div>
            {loading || waitingSeries ? <Skeleton $h={230} $r={14} /> : months.length > 0 ? <MonthlyChart months={months} animate={!series.loading} /> : (
              <div style={{ height: 230, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.85em", color: tokens.label2(theme) }}>
                Sem valores por mês neste recorte
              </div>
            )}
          </Widget>

          <CompositionWidget $delay={200}>
            {loading ? <Skeleton $h={270} $r={14} /> : <Composition kpis={kpis} />}
          </CompositionWidget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <Widget data-tour="period-map" $span={2} $delay={250} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <div className="w-title">Mapa de períodos</div>
                <div className="w-sub">Clique em um mês para filtrar a tela por ele</div>
              </div>
              <FiCalendar style={{ color: tokens.label3(theme) }} />
            </div>
            {loading || waitingSeries ? <Skeleton $h={120} $r={14} /> : months.length > 0 ? <PeriodHeatmap months={months} onSelect={onSelectMonth} /> : (
              <div style={{ fontSize: "0.85em", color: tokens.label2(theme) }}>Sem dados no período</div>
            )}
          </Widget>

          <CategoryBento
            categories={categories}
            loading={loading}
            delay={300}
            onSelect={(category) => onGoCredits({ category })}
            onSeeAll={() => onGoCredits()}
          />
        </WidgetGrid>
      </Section>

      <Section $delay={200} data-tour="rankings">
        <TopLists
          suppliers={suppliers}
          topCreditos={topCreditos}
          total={total}
          loading={loading}
          periodFiltered={periodFiltered}
          onGoSuppliers={onGoSuppliers}
          onGoCredits={onGoCredits}
          onOpenSupplier={onOpenSupplier}
          onOpenCredit={onOpenCredit}
        />
      </Section>
    </>
  );
}
